import React, { ReactElement, useMemo } from 'react';
import { Box, Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatTranslation } from 'intl';
import {
  CommonTranslationKeys,
  ReportTranslationKeys,
  UserTranslationKeys,
} from 'intl/keys';
import { useIntl } from 'react-intl';
import {
  issueViewHeaderSkeletonActionsSx,
  issueViewHeaderSkeletonCodeGroupSx,
  issueViewHeaderSkeletonCodeHeight,
  issueViewHeaderSkeletonCodeSx,
  issueViewHeaderSkeletonNavButtonSize,
  issueViewHeaderSkeletonNavButtonSx,
  issueViewHeaderSkeletonNavSx,
  issueViewHeaderSkeletonProcessTileHeight,
  issueViewHeaderSkeletonProcessTileSx,
  issueViewHeaderSkeletonProcessTileWidth,
  issueViewHeaderSkeletonRootSx,
  issueViewHeaderSkeletonToolbarBoneSx,
  issueViewHeaderSkeletonToolbarButtonSx,
  issueViewHeaderSkeletonToolbarIconSize,
  issueViewHeaderSkeletonToolbarSegmentHeight,
  issueViewHeaderSkeletonToolbarSx,
} from './styles';
import {
  estimateButtonTextIconWidth,
  measureButtonLabelWidth,
} from './estimateButtonTextIconWidth';
import {
  ISSUE_NCR_NUMBER_WIDTH_FALLBACK,
  measureIssueNcrNumberWidth,
} from './measureIssueNcrNumberWidth';
import { resolveCanvasFontFamily } from './resolveCanvasFontFamily';

const SKELETON_ANIMATION = 'wave' as const;

const TOOLBAR_BUTTON_LABEL_KEYS = [
  UserTranslationKeys.titleAccessManagement,
  CommonTranslationKeys.toolbarTitleCreateSimilar,
  ReportTranslationKeys.buttonPrintLabel,
  CommonTranslationKeys.archive,
] as const;

interface IssueViewHeaderSkeletonProps {
  issueNcrNumber?: string;
}

export function IssueViewHeaderSkeleton({
  issueNcrNumber,
}: IssueViewHeaderSkeletonProps): ReactElement {
  const intl = useIntl();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const issueNcrFontSizePx = isMdUp ? 15 : isSmUp ? 14 : 13;

  const canvasFontFamily = resolveCanvasFontFamily(
    theme.typography.fontFamily
  );

  const issueNcrNumberWidth = useMemo(
    () =>
      measureIssueNcrNumberWidth(
        issueNcrNumber ?? ISSUE_NCR_NUMBER_WIDTH_FALLBACK,
        canvasFontFamily,
        issueNcrFontSizePx
      ),
    [issueNcrNumber, canvasFontFamily, issueNcrFontSizePx]
  );

  const toolbarButtons = useMemo(() => {
    return TOOLBAR_BUTTON_LABEL_KEYS.map((key) => {
      const label = formatTranslation(intl, key);

      return {
        buttonWidth: estimateButtonTextIconWidth(label, canvasFontFamily),
        labelWidth: measureButtonLabelWidth(label, canvasFontFamily),
      };
    });
  }, [intl, canvasFontFamily]);

  return (
    <Box sx={{ width: '100%', flexShrink: 0 }}>
      <Box sx={issueViewHeaderSkeletonRootSx} data-qa='issue-view-skeleton-header'>
        <Box sx={issueViewHeaderSkeletonNavSx}>
          <Skeleton
            animation={SKELETON_ANIMATION}
            variant='circular'
            width={issueViewHeaderSkeletonNavButtonSize}
            height={issueViewHeaderSkeletonNavButtonSize}
            sx={issueViewHeaderSkeletonNavButtonSx}
          />

          <Box sx={issueViewHeaderSkeletonCodeGroupSx}>
            <Skeleton
              animation={SKELETON_ANIMATION}
              variant='rounded'
              width={issueViewHeaderSkeletonProcessTileWidth}
              height={issueViewHeaderSkeletonProcessTileHeight}
              sx={issueViewHeaderSkeletonProcessTileSx}
            />
            <Skeleton
              animation={SKELETON_ANIMATION}
              variant='rounded'
              width={issueNcrNumberWidth}
              height={issueViewHeaderSkeletonCodeHeight}
              sx={issueViewHeaderSkeletonCodeSx}
            />
          </Box>

          <Skeleton
            animation={SKELETON_ANIMATION}
            variant='circular'
            width={issueViewHeaderSkeletonNavButtonSize}
            height={issueViewHeaderSkeletonNavButtonSize}
            sx={issueViewHeaderSkeletonNavButtonSx}
          />
        </Box>

        <Box sx={issueViewHeaderSkeletonActionsSx}>
          <Box sx={issueViewHeaderSkeletonToolbarSx}>
            {toolbarButtons.map(({ buttonWidth, labelWidth }, index) => (
              <Box
                key={index}
                sx={{
                  ...issueViewHeaderSkeletonToolbarButtonSx,
                  width: buttonWidth,
                }}
              >
                <Skeleton
                  animation={SKELETON_ANIMATION}
                  variant='rounded'
                  width={issueViewHeaderSkeletonToolbarIconSize}
                  height={issueViewHeaderSkeletonToolbarIconSize}
                  sx={issueViewHeaderSkeletonToolbarBoneSx}
                />
                <Skeleton
                  animation={SKELETON_ANIMATION}
                  variant='rounded'
                  width={labelWidth}
                  height={issueViewHeaderSkeletonToolbarSegmentHeight}
                  sx={issueViewHeaderSkeletonToolbarBoneSx}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
